import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * 创建业务错误对象
 * 用于将后端 { status: 'error', error: { code, message } } 格式的响应转为 BizError
 */
function createBizError(data: any): Error {
  const error: any = new Error(data.error.message);
  error.name = 'BizError';
  error.info = {
    errorCode: data.error.code,
    errorMessage: data.error.message,
    showType: ErrorShowType.ERROR_MESSAGE,
    data: data,
  };
  // 便于 catch 块通过 error.data.error.message 获取后端消息
  error.data = data;
  return error;
}

/**
 * 判断错误是否为网络连接错误（无响应）
 */
export function isNetworkError(error: any): boolean {
  // error.request 存在但 error.response 不存在 → 请求已发出但无响应
  if (error?.request && !error?.response) return true;
  // AxiosError 的 code 字段
  if (error?.code === 'ERR_NETWORK') return true;
  // TypeError: Failed to fetch (原生 fetch)
  if (error instanceof TypeError && error.message?.includes('Network'))
    return true;
  return false;
}

/**
 * 判断错误是否为请求超时
 */
export function isTimeoutError(error: any): boolean {
  if (error?.code === 'ECONNABORTED') return true;
  if (
    error?.code === 'ERR_CANCELED' &&
    error?.message?.toLowerCase().includes('timeout')
  )
    return true;
  if (error?.message?.toLowerCase().includes('timeout')) return true;
  return false;
}

/**
 * 获取 HTTP 状态码对应的用户友好错误消息
 */
export function getHttpErrorMessage(status: number): string {
  const statusMessages: Record<number, string> = {
    400: '请求参数错误',
    // 401 由 authResponseErrorInterceptor 处理，不在此重复
    403: '没有权限访问该资源',
    404: '请求的资源不存在',
    405: '请求方法不允许',
    408: '请求超时',
    500: '服务器内部错误，请稍后重试',
    502: '网关错误',
    503: '服务暂时不可用，请稍后重试',
    504: '网关超时',
  };
  return statusMessages[status] || `请求错误 (${status})`;
}

/**
 * @name 错误处理
 * 统一请求错误处理配置
 *
 * Requirements: 11.5 - 网络错误时显示用户友好的错误通知
 *
 * 处理场景：
 * - BizError: 业务逻辑错误（后端返回 success: false）
 * - HTTP 错误: 服务器返回非 2xx 状态码（500、403、404 等）
 * - 网络错误: 请求已发出但无响应（断网、DNS 解析失败等）
 * - 超时错误: 请求超时未响应
 * - 其他错误: 请求发送阶段的未知错误
 *
 * 注意：HTTP 401 由 src/utils/request.ts 中的 authResponseErrorInterceptor 处理，
 * 此处不重复处理。
 *
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    // errorThrower 在某些 umi4 版本中可能不会被正确调用，
    // 改用 responseInterceptors 中的 bizErrorResponseInterceptor 来检测业务错误。
    // 这里保留一个空实现以兼容 umi 内部调用。
    errorThrower: () => {},
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 1. 业务逻辑错误（BizError）
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // 静默处理
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // 根据 errorCode 跳转到对应页面
              switch (errorCode) {
                case 401:
                  history.push('/user/login');
                  break;
                case 403:
                  history.push('/403');
                  break;
                case 404:
                  history.push('/404');
                  break;
                default:
                  // 其他错误显示错误消息
                  message.error(errorMessage || '请求错误');
              }
              break;
            default:
              message.error(errorMessage);
          }
        }
        return;
      }

      // 2. 超时错误
      if (isTimeoutError(error)) {
        notification.error({
          message: '请求超时',
          description: '服务器响应超时，请检查网络连接后重试',
        });
        return;
      }

      // 3. 网络错误（无响应）
      if (isNetworkError(error)) {
        notification.error({
          message: '网络连接错误',
          description: '无法连接到服务器，请检查您的网络连接',
        });
        return;
      }

      // 4. HTTP 错误（服务器返回非 2xx 状态码）
      if (error.response) {
        const { status } = error.response;
        // 401 由 authResponseErrorInterceptor 处理，此处跳过
        if (status === 401) return;

        // 尝试多种路径获取后端返回的业务错误消息
        // umi-request 把响应体放在 error.data
        // axios 把响应体放在 error.response.data
        const responseData = error.data || error.response?.data;
        const backendMessage =
          responseData?.error?.message || responseData?.message;
        const errorMessage = backendMessage || getHttpErrorMessage(status);

        if (status >= 500) {
          notification.error({
            message: `服务器错误 (${status})`,
            description: errorMessage,
          });
        } else {
          message.error(errorMessage);
        }
        return;
      }

      // 5. 其他未知错误
      message.error('请求发生未知错误，请重试');
    },
  },

  // 请求拦截器
  // 注意：Token 注入拦截器在 src/app.tsx 中通过 authReqHeaderInterceptor 添加
  requestInterceptors: [],

  // 响应拦截器
  // 注意：401 处理拦截器在 src/app.tsx 中通过 authResponseInterceptor 添加
  responseInterceptors: [
    [
      (response: any) => {
        const data = response?.data;
        if (data?.status === 'error' && data?.error?.message) {
          return Promise.reject(createBizError(data));
        }
        const { success } = (data || {}) as ResponseStructure;
        if (data && success === false && data?.errorMessage) {
          const error: any = new Error(data.errorMessage);
          error.name = 'BizError';
          error.info = {
            errorCode: data.errorCode,
            errorMessage: data.errorMessage,
            showType: data.showType,
            data: data,
          };
          error.data = data;
          return Promise.reject(error);
        }
        return response;
      },
      (error: any) => {
        const responseData = error?.response?.data;
        if (responseData?.status === 'error' && responseData?.error?.message) {
          return Promise.reject(createBizError(responseData));
        }
        return Promise.reject(error);
      },
    ],
  ],
};
